import React, { useEffect, useState } from "react";

import { Link, useLocation } from "react-router-dom";

const API_BASE_URL = (
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5000"
).replace(/\/+$/, "");

const resolveLogoUrl = (value) => {
  const cleanValue = String(value || "").trim();

  if (!cleanValue) {
    return "";
  }

  if (cleanValue.startsWith("http://") || cleanValue.startsWith("https://")) {
    return cleanValue;
  }

  if (cleanValue.startsWith("/cdn/")) {
    return `${API_BASE_URL}${cleanValue}`;
  }

  if (cleanValue.startsWith("cdn/")) {
    return `${API_BASE_URL}/${cleanValue}`;
  }

  if (cleanValue.startsWith("/")) {
    return `${API_BASE_URL}${cleanValue}`;
  }

  return `${API_BASE_URL}/cdn/${cleanValue}`;
};

const getFirstPhone = (value) => {
  return (
    String(value || "")
      .split(/[\n,;|]+/)
      .map((item) => item.trim())
      .find(Boolean) || ""
  );
};

const createPhoneHref = (phone) => {
  const cleanPhone = String(phone || "").replace(/[^\d+]/g, "");

  return cleanPhone ? `tel:${cleanPhone}` : undefined;
};

const normalizeSocialText = (value = "") => {
  return String(value)
    .toLocaleLowerCase("tr-TR")
    .replace(/ı/g, "i")
    .replace(/ğ/g, "g")
    .replace(/ü/g, "u")
    .replace(/ş/g, "s")
    .replace(/ö/g, "o")
    .replace(/ç/g, "c")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
};

const normalizeSocialUrl = (value = "") => {
  let cleanUrl = String(value).trim();

  if (!cleanUrl) {
    return "";
  }

  if (!/^https?:\/\//i.test(cleanUrl)) {
    cleanUrl = `https://${cleanUrl.replace(/^\/+/, "")}`;
  }

  try {
    const parsedUrl = new URL(cleanUrl);

    if (parsedUrl.protocol !== "http:" && parsedUrl.protocol !== "https:") {
      return "";
    }

    return parsedUrl.href;
  } catch {
    return "";
  }
};

const SOCIAL_ICON_RULES = [
  {
    keys: ["instagram"],
    icon: "fab fa-instagram",
  },
  {
    keys: ["youtube"],
    icon: "fab fa-youtube",
  },
  {
    keys: ["linkedin", "linked in"],
    icon: "fab fa-linkedin-in",
  },
  {
    keys: ["facebook"],
    icon: "fab fa-facebook-f",
  },
  {
    keys: ["twitter", "x com"],
    icon: "fab fa-twitter",
  },
  {
    keys: ["tiktok", "tik tok"],
    icon: "fab fa-tiktok",
  },
  {
    keys: ["whatsapp", "wa me"],
    icon: "fab fa-whatsapp",
  },
  {
    keys: ["telegram", "t me"],
    icon: "fab fa-telegram-plane",
  },
  {
    keys: ["pinterest"],
    icon: "fab fa-pinterest-p",
  },
  {
    keys: ["github"],
    icon: "fab fa-github",
  },
  {
    keys: ["behance"],
    icon: "fab fa-behance",
  },
  {
    keys: ["discord"],
    icon: "fab fa-discord",
  },
  {
    keys: ["spotify"],
    icon: "fab fa-spotify",
  },
  {
    keys: ["threads"],
    icon: "fas fa-at",
  },
];

const getSocialIcon = (title, link) => {
  const searchValue = normalizeSocialText(`${title || ""} ${link || ""}`);

  const matchedRule = SOCIAL_ICON_RULES.find((rule) =>
    rule.keys.some((key) => searchValue.includes(normalizeSocialText(key))),
  );

  return matchedRule?.icon || "fas fa-globe";
};
function SideMenu({ isSidebarOpen, toggleSidebar }) {
  const location = useLocation();

  const currentLang = new URLSearchParams(location.search).get("lang") || "tr";
  const withLang = (path = "") => {
    if (!path || path === "#") {
      return path || "#";
    }

    if (!currentLang || currentLang === "tr") {
      return path;
    }

    const separator = path.includes("?") ? "&" : "?";

    return `${path}${separator}lang=${encodeURIComponent(currentLang)}`;
  };
  const createSlug = (text = "") => {
    return String(text)
      .trim()
      .toLocaleLowerCase("tr-TR")
      .replace(/ç/g, "c")
      .replace(/ğ/g, "g")
      .replace(/ı/g, "i")
      .replace(/ö/g, "o")
      .replace(/ş/g, "s")
      .replace(/ü/g, "u")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  };

  const [sideMenuLogo, setSideMenuLogo] = useState("");

  const [customerServicePhone, setCustomerServicePhone] = useState("");

  const [socialLinks, setSocialLinks] = useState([]);

  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);

  const [headerMenus, setHeaderMenus] = useState([]);
  const [headerMenusLoading, setHeaderMenusLoading] = useState(true);

  const [openHeaderMenuId, setOpenHeaderMenuId] = useState(null);

  const [isProductsOpen, setIsProductsOpen] = useState(false);

  const [openProductId, setOpenProductId] = useState(null);

  const handleProductsToggle = () => {
    setIsProductsOpen((current) => !current);
  };

  useEffect(() => {
    const controller = new AbortController();

    const fetchSideMenuData = async () => {
      try {
        const [
          generalResponse,
          settingsResponse,
          contactResponse,
          socialResponse,
        ] = await Promise.all([
          fetch(
            `${API_BASE_URL}/api/general?lang=${encodeURIComponent(
              currentLang,
            )}`,
            {
              method: "GET",
              headers: {
                Accept: "application/json",
              },
              signal: controller.signal,
            },
          ),

          fetch(`${API_BASE_URL}/api/settings`, {
            method: "GET",
            headers: {
              Accept: "application/json",
            },
            signal: controller.signal,
          }),
          fetch(
            `${API_BASE_URL}/api/contact/contact-info?lang=${encodeURIComponent(
              currentLang,
            )}`,
            {
              method: "GET",
              headers: {
                Accept: "application/json",
              },
              signal: controller.signal,
            },
          ),

          fetch(
            `${API_BASE_URL}/api/social-links/social-links?lang=${encodeURIComponent(
              currentLang,
            )}`,
            {
              method: "GET",
              headers: {
                Accept: "application/json",
              },
              signal: controller.signal,
            },
          ),
        ]);

        const generalData = await generalResponse.json().catch(() => ({}));

        const contactData = await contactResponse.json().catch(() => []);

        const socialData = await socialResponse.json().catch(() => []);
        const settingsData = await settingsResponse.json().catch(() => ({}));
        if (settingsResponse.ok) {
          const settingsRecord = Array.isArray(settingsData)
            ? settingsData[0]
            : settingsData;

          setCustomerServicePhone(
            String(settingsRecord?.customer_service_phone || "").trim(),
          );
        }
        if (generalResponse.ok) {
          setSideMenuLogo(resolveLogoUrl(generalData.logo_header));
        }

        if (socialResponse.ok) {
          const normalizedSocials = (
            Array.isArray(socialData) ? socialData : []
          )
            .map((item) => {
              const title = String(
                item?.title || item?.["tıtle"] || "Sosyal Medya",
              ).trim();

              const link = normalizeSocialUrl(item?.link);

              return {
                title,
                link,
              };
            })
            .filter((item) => item.link);

          setSocialLinks(normalizedSocials);
        }
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("SIDE_MENU_DATA_ERROR:", error);
        }
      }
    };

    fetchSideMenuData();

    return () => {
      controller.abort();
    };
  }, [currentLang]);

  /*
|--------------------------------------------------------------------------
| MOBİL ÜRÜNLER MENÜSÜ
|--------------------------------------------------------------------------
*/
  useEffect(() => {
    const controller = new AbortController();

    const fetchMobileProducts = async () => {
      try {
        setProductsLoading(true);

        const response = await fetch(
          `${API_BASE_URL}/api/blogs/products/menu?lang=${encodeURIComponent(
            currentLang,
          )}`,
          {
            method: "GET",
            headers: {
              Accept: "application/json",
            },
            signal: controller.signal,
          },
        );

        if (!response.ok) {
          throw new Error(`Mobil ürün menüsü alınamadı: ${response.status}`);
        }

        const data = await response.json();

        console.log("MOBILE_PRODUCT_MENU_API:", data);

        if (Array.isArray(data)) {
          setProducts(data);
        } else if (Array.isArray(data?.data)) {
          setProducts(data.data);
        } else {
          setProducts([]);
        }
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("MOBILE_PRODUCT_MENU_API_ERROR:", error);

          setProducts([]);
        }
      } finally {
        if (!controller.signal.aborted) {
          setProductsLoading(false);
        }
      }
    };

    fetchMobileProducts();

    return () => {
      controller.abort();
    };
  }, [currentLang]);

  /*
|--------------------------------------------------------------------------
| MOBİL FRONTEND MENÜLERİ
|--------------------------------------------------------------------------
*/
  useEffect(() => {
    const controller = new AbortController();

    const fetchMobileHeaderMenus = async () => {
      try {
        setHeaderMenusLoading(true);

        const response = await fetch(
          `${API_BASE_URL}/api/frontend-menu/category-menu?lang=${encodeURIComponent(
            currentLang,
          )}`,
          {
            method: "GET",
            headers: {
              Accept: "application/json",
            },
            signal: controller.signal,
          },
        );

        if (!response.ok) {
          throw new Error(
            `Mobil frontend menüsü alınamadı: ${response.status}`,
          );
        }

        const responseData = await response.json();

        console.log("MOBILE_HEADER_MENU_API:", responseData);

        let menuData = [];

        if (Array.isArray(responseData)) {
          menuData = responseData;
        } else if (Array.isArray(responseData?.data)) {
          menuData = responseData.data;
        }

        const normalizedMenus = menuData
          .filter((menu) => {
            return menu.is_visible === true || Number(menu.is_visible) === 1;
          })
          .map((menu) => {
            const children = Array.isArray(menu.children)
              ? menu.children
                  .filter((child) => {
                    return (
                      child.is_visible === true ||
                      Number(child.is_visible) === 1
                    );
                  })
                  .sort(
                    (a, b) =>
                      Number(a.sort_order ?? 0) - Number(b.sort_order ?? 0),
                  )
              : [];

            return {
              ...menu,
              children,
            };
          })
          .sort(
            (a, b) => Number(a.sort_order ?? 0) - Number(b.sort_order ?? 0),
          );

        setHeaderMenus(normalizedMenus);
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("MOBILE_HEADER_MENU_API_ERROR:", error);

          setHeaderMenus([]);
        }
      } finally {
        if (!controller.signal.aborted) {
          setHeaderMenusLoading(false);
        }
      }
    };

    fetchMobileHeaderMenus();

    return () => {
      controller.abort();
    };
  }, [currentLang]);

  const handleProductToggle = (productId) => {
    setOpenProductId((currentId) =>
      currentId === productId ? null : productId,
    );
  };

  const handleHeaderMenuToggle = (menuId) => {
    setOpenHeaderMenuId((currentId) => (currentId === menuId ? null : menuId));
  };

  const handleMenuClose = () => {
    setIsProductsOpen(false);
    setOpenHeaderMenuId(null);

    toggleSidebar();
  };
  return (
    <div>
      <style>
        {`
          .side-bar {
            overflow: hidden;
          }

          .side-bar .rts-sidebar-menu-desktop {
            height: 100%;
            overflow-y: auto;
            overflow-x: hidden;
            padding-bottom: 35px;
          }

          .demirsan-mobile-menu {
            width: 100%;
            margin: 0 !important;
            padding: 0 !important;
            list-style: none !important;
          }

          .demirsan-mobile-menu > li {
            width: 100%;
            margin: 0 !important;
            padding: 0 !important;
            list-style: none !important;
            border-bottom: 1px solid rgba(11, 23, 53, 0.09);
          }

          .demirsan-mobile-link {
            display: flex !important;
            align-items: center;
            justify-content: space-between;
            width: 100%;
            min-height: 52px;
            margin: 0 !important;
            padding: 13px 4px !important;
            color: #0b1735 !important;
            background: transparent;
            border: none;
            font-family: inherit;
            font-size: 16px !important;
            font-weight: 600 !important;
            line-height: 1.4 !important;
            text-align: left;
            text-decoration: none !important;
          }

          .demirsan-mobile-link:hover {
            color: #1b4793 !important;
          }

          .mobile-products-button {
            cursor: pointer;
          }

          .mobile-menu-arrow {
            flex: 0 0 auto;
            width: 9px;
            height: 9px;
            margin-right: 5px;
            border-right: 2px solid currentColor;
            border-bottom: 2px solid currentColor;
            transform: rotate(45deg);
            transition: transform 0.22s ease;
          }

          .mobile-menu-arrow.open {
            transform: rotate(225deg);
          }

          /* Ürünler listesi */
          .mobile-products-list {
            display: block !important;
            width: 100%;
            max-height: 0;
            margin: 0 !important;
            padding: 0 !important;
            overflow: hidden;
            list-style: none !important;
            background: #f6f7f9;
            transition:
              max-height 0.35s ease,
              padding 0.35s ease;
          }

          .mobile-products-list.open {
            max-height: 3000px;
            padding: 7px 0 !important;
          }

          .mobile-product-item {
            display: block !important;
            width: 100%;
            margin: 0 !important;
            padding: 0 !important;
            list-style: none !important;
          }

          .mobile-product-button {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 12px;
            width: 100%;
            min-height: 48px;
            margin: 0;
            padding: 11px 15px 11px 20px;
            color: #18213a;
            background: transparent;
            border: none;
            border-bottom: 1px solid rgba(11, 23, 53, 0.07);
            font-family: inherit;
            font-size: 15px;
            font-weight: 600;
            line-height: 1.4;
            text-align: left;
            cursor: pointer;
            transition:
              color 0.2s ease,
              background-color 0.2s ease,
              padding-left 0.2s ease;
          }

          .mobile-product-button:hover,
          .mobile-product-button.active {
            padding-left: 25px;
            color: #ffffff;
            background: #1b4793;
          }

          .mobile-product-arrow {
            flex: 0 0 auto;
            width: 8px;
            height: 8px;
            margin-right: 3px;
            border-right: 2px solid currentColor;
            border-bottom: 2px solid currentColor;
            transform: rotate(45deg);
            transition: transform 0.22s ease;
          }

          .mobile-product-arrow.open {
            transform: rotate(225deg);
          }

          /* Uygulamalar listesi */
          .mobile-applications-list {
            display: block !important;
            width: 100%;
            max-height: 0;
            margin: 0 !important;
            padding: 0 !important;
            overflow: hidden;
            list-style: none !important;
            background: #ffffff;
            transition:
              max-height 0.35s ease,
              padding 0.35s ease;
          }

          .mobile-applications-list.open {
            max-height: 900px;
            padding: 5px 0 !important;
            overflow-y: auto;
          }

          .mobile-application-item {
            display: block !important;
            width: 100%;
            margin: 0 !important;
            padding: 0 !important;
            list-style: none !important;
          }

          .mobile-application-link {
            display: block !important;
            width: 100%;
            min-height: 43px;
            margin: 0 !important;
            padding: 10px 20px 10px 36px !important;
            color: #41495b !important;
            background: #ffffff;
            border-bottom: 1px solid rgba(11, 23, 53, 0.06);
            font-size: 14px !important;
            font-weight: 500 !important;
            line-height: 1.45 !important;
            text-decoration: none !important;
            transition:
              color 0.2s ease,
              background-color 0.2s ease,
              padding-left 0.2s ease;
          }

          .mobile-application-link::before {
            content: "–";
            margin-right: 8px;
            color: #1b4793;
          }

          .mobile-application-link:hover {
            padding-left: 42px !important;
            color: #ffffff !important;
            background: #1b4793;
          }

          .mobile-application-link:hover::before {
            color: #ffffff;
          }

          /*
           * Uzun uygulama listesinin scrollbar görünümü
           */
          .mobile-applications-list.open::-webkit-scrollbar {
            width: 6px;
          }

          .mobile-applications-list.open::-webkit-scrollbar-track {
            background: #eeeeee;
          }

          .mobile-applications-list.open::-webkit-scrollbar-thumb {
            background: #9b9b9b;
            border-radius: 10px;
          }

          .mobile-applications-list.open {
            scrollbar-width: thin;
            scrollbar-color: #9b9b9b #eeeeee;
          }

          /*
           * Temanın mobil alt menülere eklediği
           * varsayılan ok ve çizgileri kaldır
           */
          .demirsan-mobile-menu a::before,
          .demirsan-mobile-menu a::after {
            display: none !important;
            content: none !important;
          }

          .sidebar-social-area {
            margin-top: 28px;
            padding-top: 24px;
            border-top: 1px solid rgba(11, 23, 53, 0.12);
          }

          .sidebar-social-title {
            display: block;
            margin-bottom: 14px;
            color: #0b1735;
            font-size: 15px;
            font-weight: 600;
          }

          .sidebar-social-list {
            display: flex;
            align-items: center;
            gap: 10px;
            margin: 0;
            padding: 0;
            list-style: none;
          }

          .sidebar-social-list li {
            margin: 0;
            padding: 0;
          }

          .sidebar-social-list li a {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 42px;
            height: 42px;
            color: #e60000;
            background-color: #fff0f0;
            border: 1px solid rgba(230, 0, 0, 0.18);
            border-radius: 50%;
            font-size: 17px;
            text-decoration: none;
            transition:
              background-color 0.2s ease,
              color 0.2s ease,
              border-color 0.2s ease,
              transform 0.2s ease;
          }

          .sidebar-social-list li a:hover {
            color: #ffffff;
            background-color: #e60000;
            border-color: #e60000;
            transform: translateY(-2px);
          }

          @media (max-width: 575.98px) {
            .demirsan-mobile-link {
              min-height: 49px;
              font-size: 15px !important;
            }

            .mobile-product-button {
              min-height: 46px;
              padding-left: 16px;
              font-size: 14px;
            }

            .mobile-product-button:hover,
            .mobile-product-button.active {
              padding-left: 21px;
            }

            .mobile-application-link {
              padding-left: 29px !important;
              font-size: 13px !important;
            }

            .mobile-application-link:hover {
              padding-left: 35px !important;
            }
          }
            .mobile-products-button,
.mobile-product-button {
  width: 100% !important;
}

.mobile-products-button > span:first-child,
.mobile-product-button > span:first-child {
  flex: 1;
  min-width: 0;
  padding-right: 16px;
}

.mobile-menu-arrow,
.mobile-product-arrow {
  margin-left: auto !important;
  margin-right: 8px !important;
  flex-shrink: 0;
}
  .mobile-products-list {
  width: 100% !important;
  background: #f6f7f9;
}

.mobile-product-item {
  width: 100% !important;
}

.mobile-product-button {
  display: flex !important;
  align-items: center;
  justify-content: space-between;

  width: 100% !important;
  min-height: 52px;

  margin: 0 !important;
  padding: 12px 18px !important;

  box-sizing: border-box;

  color: #18213a;
  background: transparent;

  border: none;
  border-bottom: 1px solid rgba(11, 23, 53, 0.08);

  text-align: left;
}

.mobile-product-button > span:first-child {
  flex: 1;
  min-width: 0;
  padding-right: 16px;
}

.mobile-product-arrow {
  flex: 0 0 9px;
  width: 9px;
  height: 9px;

  margin-left: 16px !important;
  margin-right: 2px !important;
}

.mobile-product-button:hover,
.mobile-product-button.active {
  width: 100% !important;
  padding-left: 23px !important;

  color: #ffffff;
  background: #1b4793;
}

/* Son ürünün alt çizgisini kaldır */
.mobile-product-item:last-child .mobile-product-button {
  border-bottom: none;
}
  .mobile-product-button {
  position: relative;

  display: flex !important;
  align-items: center;

  width: 100% !important;
  min-height: 52px;

  padding: 12px 50px 12px 18px !important;
  box-sizing: border-box;
}

.mobile-product-button > span:first-child {
  display: block;
  width: 100%;
  min-width: 0;
  padding: 0;
}

.mobile-product-arrow {
  position: absolute;
  top: 50%;
  right: 20px;

  width: 9px;
  height: 9px;

  margin: 0 !important;

  transform: translateY(-65%) rotate(45deg);
}

.mobile-product-arrow.open {
  transform: translateY(-35%) rotate(225deg);
}
  /* MOBİL ÜRÜN MENÜSÜNÜ KESİN OLARAK TAM GENİŞLİĞE ZORLA */
.side-bar .demirsan-mobile-menu,
.side-bar .mobile-products-list,
.side-bar .mobile-products-list > .mobile-product-item {
  display: block !important;
  width: 100% !important;
  min-width: 100% !important;
  max-width: none !important;
  margin: 0 !important;
  box-sizing: border-box !important;
  float: none !important;
}

/* Ürünlerin bulunduğu gri alan */
.side-bar .mobile-products-list.open {
  padding: 7px 0 !important;
  background: #f6f7f9;
}

/* HER ÜRÜN BUTONU AYNI GENİŞLİKTE */
.side-bar
  .mobile-products-list
  > .mobile-product-item
  > .mobile-product-button {
  position: relative !important;

  display: grid !important;
  grid-template-columns: minmax(0, 1fr) 14px !important;
  align-items: center !important;
  column-gap: 16px !important;

  width: 100% !important;
  min-width: 100% !important;
  max-width: none !important;
  min-height: 52px !important;

  margin: 0 !important;
  padding: 12px 20px !important;

  box-sizing: border-box !important;
  float: none !important;

  color: #18213a !important;
  background: transparent;

  border: none !important;
  border-bottom: 1px solid rgba(11, 23, 53, 0.08) !important;

  text-align: left !important;
}

/* Ürün adı */
.side-bar
  .mobile-product-button
  > span:first-child {
  display: block !important;
  width: auto !important;
  min-width: 0 !important;

  margin: 0 !important;
  padding: 0 !important;

  overflow-wrap: break-word;
}

/* BÜTÜN OKLAR AYNI SAĞ HİZADA */
.side-bar
  .mobile-product-button
  > .mobile-product-arrow {
  position: static !important;

  display: block !important;
  justify-self: end !important;

  width: 9px !important;
  height: 9px !important;

  margin: 0 3px 0 0 !important;

  border-right: 2px solid currentColor !important;
  border-bottom: 2px solid currentColor !important;

  transform: rotate(45deg) !important;
}

/* Açık ürün oku */
.side-bar
  .mobile-product-button
  > .mobile-product-arrow.open {
  transform: rotate(225deg) !important;
}

/* Seçili ürünün mavi arka planı da tam genişlikte */
.side-bar
  .mobile-products-list
  > .mobile-product-item
  > .mobile-product-button:hover,
.side-bar
  .mobile-products-list
  > .mobile-product-item
  > .mobile-product-button.active {
  width: 100% !important;
  min-width: 100% !important;

  padding: 12px 20px !important;

  color: #ffffff !important;
  background: #1b4793 !important;
}

/* Son ürünün alt çizgisini kaldır */
.side-bar
  .mobile-products-list
  > .mobile-product-item:last-child
  > .mobile-product-button {
  border-bottom: none !important;
}
  /* Ürünler ve Uygulamalar başlığını diğer ana menülerle hizala */
.side-bar .mobile-products-button {
  display: grid !important;
  grid-template-columns: minmax(0, 1fr) 14px !important;
  align-items: center !important;
  column-gap: 16px !important;

  width: 100% !important;
  min-width: 100% !important;

  /* Ana Sayfa, Kurumsal ve Kalite ile aynı sol hizaya gelir */
  padding: 13px 4px !important;

  box-sizing: border-box !important;
  text-align: left !important;
}

.side-bar .mobile-products-button > span:first-child {
  display: block !important;
  min-width: 0 !important;
  margin: 0 !important;
  padding: 0 !important;
}

.side-bar .mobile-products-button > .mobile-menu-arrow {
  position: static !important;
  display: block !important;
  justify-self: end !important;

  width: 9px !important;
  height: 9px !important;

  margin: 0 8px 0 0 !important;

  border-right: 2px solid currentColor !important;
  border-bottom: 2px solid currentColor !important;

  transform: rotate(45deg) !important;
}

.side-bar .mobile-products-button > .mobile-menu-arrow.open {
  transform: rotate(225deg) !important;
}
  #side-bar .close-icon-menu {
  position: absolute !important;
  top: 16px !important;
  right: 16px !important;
  left: auto !important;

  width: 30px !important;
  height: 30px !important;
  min-width: 30px !important;
  min-height: 30px !important;
  max-width: 30px !important;
  max-height: 30px !important;

  display: flex !important;
  align-items: center !important;
  justify-content: center !important;

  margin: 0 !important;
  padding: 0 !important;

  border: 0 !important;
  border-radius: 999px !important;

  background: #e60000 !important;
  color: #ffffff !important;

  line-height: 1 !important;
  overflow: hidden !important;

  box-shadow: 0 4px 10px rgba(230, 0, 0, 0.22) !important;

  z-index: 999 !important;
}

#side-bar .close-icon-menu i {
  display: block !important;

  width: auto !important;
  height: auto !important;

  margin: 0 !important;
  padding: 0 !important;

  color: #ffffff !important;
  font-size: 12px !important;
  line-height: 1 !important;

  transform: none !important;
}

#side-bar .close-icon-menu::before,
#side-bar .close-icon-menu::after {
  display: none !important;
  content: none !important;
}

#side-bar .close-icon-menu:hover {
  border-radius: 999px !important;
  background: #bd0000 !important;
  transform: scale(1.05);
}
  .sidebar-social-area {
  margin-top: 28px;
  padding-top: 24px;
  border-top: none !important;
}
  .sidebar-customer-service {
  display: flex;
  align-items: center;
  gap: 13px;

  width: 100%;
  margin-top: 18px;
  padding: 18px 4px;

  border-top: 1px solid rgba(11, 23, 53, 0.1);
  border-bottom: 1px solid rgba(11, 23, 53, 0.1);
}

.sidebar-customer-service-icon {
  display: flex;
  align-items: center;
  justify-content: center;

  width: 46px;
  height: 46px;
  min-width: 46px;

  color: #c4265a;
  background: rgba(196, 38, 90, 0.08);

  border-radius: 10px;

  font-size: 28px;
  line-height: 1;
}

.sidebar-customer-service-icon img {
  display: block;

  width: 42px;
  height: 42px;

  object-fit: contain;
}

.sidebar-customer-service-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  min-width: 0;
}

.sidebar-customer-service-title {
  display: block;

  margin-bottom: 4px;

  color: #0b1735;

  font-size: 15px;
  font-weight: 700;
  line-height: 1.3;
}

.sidebar-customer-service-phone {
  display: block;

  color: #c4265a !important;

  font-size: 15px !important;
  font-weight: 700 !important;
  line-height: 1.3 !important;

  text-decoration: none !important;
  white-space: nowrap;
}

.sidebar-customer-service-phone:hover {
  color: #97183e !important;
}

.sidebar-customer-service-phone::before,
.sidebar-customer-service-phone::after {
  display: none !important;
  content: none !important;
}

/* Müşteri hizmetlerinden sonra sosyal medya alanı */
.sidebar-customer-service + .sidebar-social-area {
  margin-top: 18px;
  padding-top: 0;
  border-top: none !important;
}

@media (max-width: 575.98px) {
  .sidebar-customer-service {
    gap: 11px;
    padding: 16px 4px;
  }

  .sidebar-customer-service-icon {
    width: 42px;
    height: 42px;
    min-width: 42px;

    font-size: 25px;
  }

  .sidebar-customer-service-title,
  .sidebar-customer-service-phone {
    font-size: 14px !important;
  }
}
  .sidebar-customer-service-icon {
  display: flex;
  align-items: center;
  justify-content: center;

  width: 46px;
  height: 46px;
  min-width: 46px;

  color: #e3262e;
  background: transparent;

  border: none;
  border-radius: 0;
  box-shadow: none;

  font-size: 30px;
  font-weight: 300;
  line-height: 1;
}

.sidebar-customer-service-icon i {
  color: #e3262e !important;
  font-weight: 300 !important;
}
  .sidebar-customer-service-phone {
  display: block;

  color: #e3262e !important;

  font-size: 15px !important;
  font-weight: 600 !important;
  line-height: 1.3 !important;

  text-decoration: none !important;
  white-space: nowrap;
}

.sidebar-customer-service-phone:hover {
  color: #ed0b0b !important;
}
  /*
|--------------------------------------------------------------------------
| MOBİL DİNAMİK FRONTEND ALT MENÜ
|--------------------------------------------------------------------------
*/

.mobile-header-submenu {
  display: block !important;

  width: 100%;
  max-height: 0;

  margin: 0 !important;
  padding: 0 !important;

  overflow: hidden;

  list-style: none !important;

  background: #f6f7f9;

  transition:
    max-height 0.35s ease,
    padding 0.35s ease;
}

.mobile-header-submenu.open {
  max-height: 1500px;

  padding: 7px 0 !important;
}

.mobile-header-submenu-item {
  display: block !important;

  width: 100%;

  margin: 0 !important;
  padding: 0 !important;

  list-style: none !important;
}

.mobile-header-submenu-link {
  display: flex !important;

  align-items: center;

  width: 100%;
  min-height: 48px;

  margin: 0 !important;
  padding: 11px 20px !important;

  box-sizing: border-box;

  color: #18213a !important;
  background: transparent;

  border-bottom:
    1px solid
    rgba(11, 23, 53, 0.07);

  font-family: inherit;
  font-size: 15px !important;
  font-weight: 600 !important;
  line-height: 1.4 !important;

  text-align: left;
  text-decoration: none !important;

  transition:
    color 0.2s ease,
    background-color 0.2s ease,
    padding-left 0.2s ease;
}

.mobile-header-submenu-item:last-child
  .mobile-header-submenu-link {
  border-bottom: none;
}

.mobile-header-submenu-link:hover {
  padding-left: 25px !important;

  color: #ffffff !important;
  background: #1b4793;
}

.mobile-menu-loading {
  display: flex;

  align-items: center;

  width: 100%;
  min-height: 46px;

  padding: 10px 20px;

  box-sizing: border-box;

  color: #6b7280;

  font-size: 14px;
}

.mobile-main-menu-loading {
  padding-left: 4px;
}
  /*
|--------------------------------------------------------------------------
| MOBİL MENÜ SEVİYELERİNİ EŞİTLE
|--------------------------------------------------------------------------
|
| 2. seviye:
| - Ürün kategorileri
| - Kurumsal alt sayfaları
|
| Bunlar birebir aynı görünür.
*/

.side-bar .mobile-product-button,
.side-bar .mobile-header-submenu-link {
  display: grid !important;
  grid-template-columns: minmax(0, 1fr) 14px !important;
  align-items: center !important;

  width: 100% !important;
  min-width: 100% !important;

  min-height: 52px !important;

  margin: 0 !important;
  padding: 12px 20px !important;

  box-sizing: border-box !important;

  color: #18213a !important;
  background: transparent;

  border: none !important;
  border-bottom: 1px solid rgba(11, 23, 53, 0.08) !important;

  font-family: inherit !important;
  font-size: 15px !important;
  font-weight: 600 !important;
  line-height: 1.4 !important;

  text-align: left !important;
  text-decoration: none !important;
}


/*
|--------------------------------------------------------------------------
| HOVER / AKTİF
|--------------------------------------------------------------------------
*/

.side-bar .mobile-product-button:hover,
.side-bar .mobile-product-button.active,
.side-bar .mobile-header-submenu-link:hover {
  color: #ffffff !important;
  background: #1b4793 !important;

  padding: 12px 20px !important;
}


/*
|--------------------------------------------------------------------------
| SON SATIR ÇİZGİSİ
|--------------------------------------------------------------------------
*/

.side-bar
  .mobile-product-item:last-child
  .mobile-product-button,
.side-bar
  .mobile-header-submenu-item:last-child
  .mobile-header-submenu-link {
  border-bottom: none !important;
}


/*
|--------------------------------------------------------------------------
| TELEFON
|--------------------------------------------------------------------------
*/

@media (max-width: 575.98px) {
  .side-bar .mobile-product-button,
  .side-bar .mobile-header-submenu-link {
    min-height: 46px !important;

    padding: 10px 16px !important;

    font-size: 14px !important;
    font-weight: 600 !important;
    line-height: 1.4 !important;
  }

  .side-bar .mobile-product-button:hover,
  .side-bar .mobile-product-button.active,
  .side-bar .mobile-header-submenu-link:hover {
    padding: 10px 16px !important;
  }
}
  /* SIDEBAR OVERLAY'İN ÜSTÜNDE KALSIN */
#side-bar {
  position: fixed !important;
  z-index: 100002 !important;
  pointer-events: auto !important;
}

#side-bar.show {
  pointer-events: auto !important;
}

/* KARARTMA SIDEBAR'IN ALTINDA KALSIN */
#anywhere-home {
  z-index: 100001 !important;
}
        `}
      </style>

      <div id="side-bar" className={`side-bar ${isSidebarOpen ? "show" : ""}`}>
        <button
          type="button"
          className="close-icon-menu"
          aria-label="Menüyü kapat"
          onClick={handleMenuClose}
        >
          <i className="far fa-times" />
        </button>

        <div className="rts-sidebar-menu-desktop">
          <Link
            to="/"
            onClick={handleMenuClose}
            style={{
              display: "inline-block",
              marginBottom: "6px",
              marginTop: "8px",
            }}
          >
            <img
              src={sideMenuLogo || "/assets/images/logo/logo-2.png"}
              alt="Demirsan Logo"
              style={{
                height: "60px",
                width: "auto",
                maxWidth: "190px",
                objectFit: "contain",
              }}
              onError={(event) => {
                event.currentTarget.onerror = null;

                event.currentTarget.src = "/assets/images/logo/logo-2.png";
              }}
            />
          </Link>
          <div className="body-mobile d-block">
            <nav className="nav-main mainmenu-nav">
              <ul className="demirsan-mobile-menu" id="mobile-menu-active">
                <li>
                  <Link
                    className="demirsan-mobile-link"
                    to="/"
                    onClick={handleMenuClose}
                  >
                    Ana Sayfa
                  </Link>
                </li>

                {/* =====================================================
    ÜRÜNLER VE UYGULAMALAR - DİNAMİK
====================================================== */}
                <li>
                  <button
                    type="button"
                    className="demirsan-mobile-link mobile-products-button"
                    onClick={handleProductsToggle}
                    aria-expanded={isProductsOpen}
                    aria-controls="mobile-products-list"
                  >
                    <span>Ürünler</span>

                    <span
                      className={`mobile-menu-arrow ${
                        isProductsOpen ? "open" : ""
                      }`}
                      aria-hidden="true"
                    />
                  </button>

                  <ul
                    id="mobile-products-list"
                    className={`mobile-products-list ${
                      isProductsOpen ? "open" : ""
                    }`}
                  >
                    {productsLoading && (
                      <li className="mobile-product-item">
                        <div className="mobile-menu-loading">Yükleniyor...</div>
                      </li>
                    )}

                    {!productsLoading && products.length === 0 && (
                      <li className="mobile-product-item">
                        <div className="mobile-menu-loading">
                          Ürün bulunamadı.
                        </div>
                      </li>
                    )}

                    {!productsLoading &&
                      products.map((category) => {
                        const categoryTitle =
                          category.title ||
                          category.name ||
                          `Kategori ${category.id}`;

                        const categorySlug = `${createSlug(
                          categoryTitle,
                        )}-${category.id}`;

                        return (
                          <li className="mobile-product-item" key={category.id}>
                            <Link
                              className="mobile-product-button"
                              to={withLang(`/our-service/${categorySlug}`)}
                              onClick={handleMenuClose}
                            >
                              <span>{categoryTitle}</span>
                            </Link>
                          </li>
                        );
                      })}
                  </ul>
                </li>

                {/* =====================================================
                            FRONTEND MENÜLERİ - DİNAMİK
                        ====================================================== */}

                {headerMenusLoading && (
                  <li>
                    <div className="mobile-menu-loading mobile-main-menu-loading">
                      Menü yükleniyor...
                    </div>
                  </li>
                )}

                {!headerMenusLoading &&
                  headerMenus.map((menu) => {
                    const children = Array.isArray(menu.children)
                      ? menu.children
                      : [];

                    const hasChildren = children.length > 0;

                    const isOpen = Number(openHeaderMenuId) === Number(menu.id);

                    const menuUrl = menu.url || "#";

                    /*
                     * Alt menüsü yoksa direkt link.
                     *
                     * Kalite
                     * İnsan Kaynakları
                     * İletişim
                     */
                    if (!hasChildren) {
                      if (menuUrl === "#") {
                        return (
                          <li key={menu.id}>
                            <button
                              type="button"
                              className="demirsan-mobile-link"
                            >
                              {menu.title}
                            </button>
                          </li>
                        );
                      }

                      return (
                        <li key={menu.id}>
                          <Link
                            className="demirsan-mobile-link"
                            to={withLang(menuUrl)}
                            onClick={handleMenuClose}
                            data-url-locked={menu.url_locked ?? 0}
                          >
                            {menu.title}
                          </Link>
                        </li>
                      );
                    }

                    /*
                     * Alt menüsü varsa accordion.
                     *
                     * Kurumsal
                     *    Hakkımızda
                     *    Biz Kimiz?
                     *    ...
                     */
                    return (
                      <li key={menu.id}>
                        <button
                          type="button"
                          className="demirsan-mobile-link mobile-products-button"
                          onClick={() => handleHeaderMenuToggle(menu.id)}
                          aria-expanded={isOpen}
                          aria-controls={`mobile-header-menu-${menu.id}`}
                        >
                          <span>{menu.title}</span>

                          <span
                            className={`mobile-menu-arrow ${
                              isOpen ? "open" : ""
                            }`}
                            aria-hidden="true"
                          />
                        </button>

                        <ul
                          id={`mobile-header-menu-${menu.id}`}
                          className={`mobile-header-submenu ${
                            isOpen ? "open" : ""
                          }`}
                        >
                          {children.map((child) => {
                            const childUrl = child.url || "#";

                            return (
                              <li
                                className="mobile-header-submenu-item"
                                key={child.id}
                              >
                                {childUrl !== "#" ? (
                                  <Link
                                    className="mobile-header-submenu-link"
                                    to={withLang(childUrl)}
                                    onClick={handleMenuClose}
                                    data-url-locked={child.url_locked ?? 0}
                                  >
                                    {child.title}
                                  </Link>
                                ) : (
                                  <span className="mobile-header-submenu-link">
                                    {child.title}
                                  </span>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      </li>
                    );
                  })}
              </ul>
            </nav>

            {customerServicePhone && (
              <div className="sidebar-customer-service">
                <div
                  className="sidebar-customer-service-icon"
                  aria-hidden="true"
                >
                  <i className="fal fa-headset" />
                </div>

                <div className="sidebar-customer-service-content">
                  <span className="sidebar-customer-service-title">
                    Müşteri Hizmetleri
                  </span>

                  <a
                    className="sidebar-customer-service-phone"
                    href={createPhoneHref(customerServicePhone)}
                  >
                    {customerServicePhone}
                  </a>
                </div>
              </div>
            )}

            {socialLinks.length > 0 && (
              <div className="sidebar-social-area">
                <span className="sidebar-social-title">Bizi Takip Edin</span>

                <ul className="sidebar-social-list">
                  {socialLinks.map((social, index) => (
                    <li key={`${social.title}-${social.link}-${index}`}>
                      <a
                        href={social.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`Demirsan ${social.title}`}
                        title={social.title}
                      >
                        <i
                          className={getSocialIcon(social.title, social.link)}
                        />
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>

      <div
        id="anywhere-home"
        className={isSidebarOpen ? "bgshow" : ""}
        onClick={handleMenuClose}
      />
    </div>
  );
}

export default SideMenu;
